import { useCallback, useRef, useState } from 'react';
import { Button, IconHighlight, ModalLayout, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Trash2 } from 'lucide-react';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { CENTERED_PANEL, WIDE_MODAL } from '../shared/modalClasses';

const TITLE_ID = 'delete-group-title';

/**
 * Confirm modal of "Delete group". Deleting a group never touches its members or their access,
 * and the copy says so. Failure keeps the modal open with an error toast.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} props.groupName
 * @param {() => void} props.onClose
 * @param {() => Promise<void>} props.onConfirm Rejects with the API message on failure.
 */
function DeleteGroupModal({ isOpen, groupName, onClose, onConfirm }) {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);

  const handleClose = useCallback(() => {
    if (!deletingRef.current) onClose();
  }, [onClose]);

  useDialogKeyboard(isOpen, handleClose, TITLE_ID);

  const handleConfirm = useCallback(async () => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await onConfirm();
      toast.success({
        title: t('groups.delete.successTitle'),
        message: t('groups.delete.successMessage', { name: groupName }),
        canClose: true,
      });
      onClose();
    } catch (err) {
      toast.error({
        title: t('groups.delete.errorTitle'),
        message: err?.message ?? t('groups.errors.generic'),
        canClose: true,
      });
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  }, [onConfirm, onClose, toast, t, groupName]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      titleId={TITLE_ID}
      closeDisabled={deleting}
      closeAriaLabel={t('common.close')}
      useBodyPortal
      titlePrefix={<IconHighlight icon={Trash2} bgColor="Error" iconColor="Error" size="medium" />}
      title={t('groups.delete.title', { name: groupName })}
      panelClassName={CENTERED_PANEL}
      maxWidthClassName={WIDE_MODAL}
    >
      <p className="px-6 pb-2 text-center text-sm text-content-secondary">
        {t('groups.delete.warning', { name: groupName })}
      </p>
      <div className="flex justify-center gap-2 px-6 pb-6 pt-2">
        <Button variant="secondary" size="md" data-autofocus disabled={deleting} onClick={handleClose}>
          {t('groups.delete.cancel')}
        </Button>
        <Button variant="danger" size="md" loading={deleting} onClick={handleConfirm}>
          {t('groups.delete.confirm')}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default DeleteGroupModal;
